import { Box, Html } from "@react-three/drei";
import { useDispatch, useSelector } from "react-redux";
import styles from "./InteractionPoint.module.css";
import { useRef, useEffect } from "react";
import { useGSAP } from "@gsap/react";
import { gsap } from "gsap";
import data from "./InteractionPoint.json";
import { setCurrentModule } from "../../store/reducers/progressionReducer";
import { logEvent } from "../../analytics";

export default function InteractionPoint({ module, position, args, name }) {
  const currentState = useSelector((state) => state.progression.currentState);
  const currentModule = useSelector((state) => state.progression.currentModule);
  const nextState = useSelector((state) => state.progression.nextState);
  const dispatch = useDispatch();

  const tlRef = useRef();
  const markerRef = useRef();
  const markerRefTitle = useRef();

  // Référence pour suivre si c'est le premier rendu
  const isInitialRender = useRef(true);

  const { contextSafe } = useGSAP();

  // Effet pour initialiser les points comme invisibles
  useEffect(() => {
    if (markerRef.current && isInitialRender.current) {
      // Assure que les points sont invisibles au premier rendu
      gsap.set(markerRef.current, { scale: 0, opacity: 0, pointerEvents: "none" });
      gsap.set(markerRefTitle.current, { scale: 0, opacity: 0 });
      isInitialRender.current = false;
    }
  }, []);

  useGSAP(
    () => {
      if (markerRef.current) {
        if (
          currentModule === "none" &&
          data[module].state === currentState &&
          nextState === data[module].state
        ) {
          // Délai pour éviter l'apparition immédiate au chargement
          setTimeout(() => {
            animateIn();
          }, 500);
        } else {
          animateOut();
        }
      }
    },
    { dependencies: [currentModule, currentState, nextState] },
  );

  const animateIn = contextSafe(() => {
    tlRef.current?.kill();
    tlRef.current = gsap.timeline().fromTo(
      markerRef.current,
      { scale: 0, opacity: 0, pointerEvents: "none" },
      {
        scale: 1,
        opacity: 1,
        pointerEvents: "auto",
        duration: 0.75,
        ease: "back.out(1.4)",
      },
    ).fromTo(
      markerRefTitle.current,
      { scale: 0, opacity: 0 },
      {
        scale: 1,
        opacity: 1,
        duration: 0.75,
        ease: "back.out(1.4)",
      },
      "<"
    );
  });

  const animateOut = contextSafe(() => {
    tlRef.current?.kill();
    tlRef.current = gsap.timeline().to(markerRef.current, {
      scale: 0,
      opacity: 0,
      duration: 0.5,
      pointerEvents: "none",
      ease: "power2.out",
    }).to(markerRefTitle.current, {
      scale: 0,
      opacity: 0,
      duration: 0.5,
      ease: "power2.out",
    }, "<");
  });

  const handleModuleClick = () => {
    if (data[module].analytics) {
      logEvent("Module", "click", data[module].analytics);
    }
    if (data[module].src) {
      window.open(data[module].src, '_blank');
    } else {
      dispatch(setCurrentModule(module));
    }
  };

  return (
    <Box
      position={position}
      args={args}
      visible={false}
      onClick={handleModuleClick}
    >
      <Html sprite distanceFractor={0.5}>
        <button
          className={styles.marker}
          ref={markerRef}
          onClick={handleModuleClick}
          style={{ opacity: 0, transform: "translate(-50%, -50%) scale(0)" }}
        >
        </button>
        <p
          ref={markerRefTitle}
          className={styles.markertitle}
          style={{ opacity: 0, transform: "translateX(-50%) scale(0)" }}
        >
          {name}
        </p>
      </Html>
    </Box>
  );
}
